import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import { formatApiErrors, hasError } from '../../utils/formatError';
import { fNumber } from '../../utils/formatNumber';
import { csvHeader, templateFor } from '../../utils/walletRequisites';

// The server enforces the real limit (contract: 1 MB). This only spares an
// upload that is bound to be refused, so it is the lenient reading of "1 MB".
const MAX_FILE_BYTES = 1024 * 1024;

// Enough for every terminal an installation realistically has; the list is
// fetched once per dialog and filtered by gateway here.
const TERMINALS_PAGE_SIZE = 1000;

// Up to this many skipped rows are listed straight away; more stay folded.
const SKIPPED_SHOWN_OPEN = 5;

const STAGE = { CHECK: 'check', IMPORT: 'import' };

const EXAMPLE_CSV = 'card_number;holder;is_active\n2200123456789012;IVAN PETROV;true';

// A row of the example for a gateway with a requisites template, by field key.
const EXAMPLE_VALUES = {
    card_number: '4111111111111111',
    holder: 'IVAN PETROV',
    bank: 'Kapitalbank',
    phone: '+998901234567',
    address: 'TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE',
    network: 'TRC20'
};

/** The example file for a gateway: its template's columns when it has one. */
export const exampleCsv = (template) =>
    template
        ? `${csvHeader(template)}\n${[...template.fields.map((f) => EXAMPLE_VALUES[f.key] ?? ''), 'true'].join(';')}`
        : EXAMPLE_CSV;

const sameId = (a, b) =>
    a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

/**
 * A wallet pinned to a terminal must use one of that terminal's gateways —
 * payin or payout — or the backend refuses the import ("does not use gateway").
 * The light terminal DTO carries no gateway ids, hence the full rows.
 */
const terminalUsesGateway = (terminal, gatewayId) =>
    sameId(terminal.payinGateway?.id ?? terminal.payinGatewayId, gatewayId) ||
    sameId(terminal.payoutGateway?.id ?? terminal.payoutGatewayId, gatewayId);

const byName = (a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''));

/** The terminals a wallet on `gatewayId` may be pinned to, by name. */
export const terminalsForGateway = (terminals, gatewayId) =>
    gatewayId === null || gatewayId === undefined
        ? []
        : (terminals || []).filter((item) => item && terminalUsesGateway(item, gatewayId)).sort(byName);

/**
 * Whether a check result clears the way to import: a dry run, no row errors (the
 * import is all or nothing) and something new to create.
 */
export const isImportReady = (result) =>
    Boolean(result) && result.dryRun === true && result.errors.length === 0 && result.toCreate > 0;

/** The import response's `data`, with every field the dialog reads present. */
export const normalizeResult = (data) => {
    const raw = data || {};
    const list = (value) => (Array.isArray(value) ? value : []);
    return {
        dryRun: raw.dryRun === true,
        rows: Number(raw.rows) || 0,
        toCreate: Number(raw.toCreate) || 0,
        created: Number(raw.created) || 0,
        skipped: list(raw.skipped),
        errors: list(raw.errors),
        receiverInfoKeys: list(raw.receiverInfoKeys)
    };
};

const RowMessagesTable = ({ rows }) => (
    <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 80 }}>{i18n.t('walletCsv.row')}</TableCell>
                    <TableCell>{i18n.t('walletCsv.message')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((item, index) => (
                    <TableRow key={`${item.row}-${index}`}>
                        <TableCell>{item.row}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>{item.message}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

/**
 * Bulk-create wallets on one gateway from a CSV.
 *
 * Two calls to the same endpoint: Check (dryRun) parses and validates the file
 * and says what would happen; Import repeats it for real. The import is all or
 * nothing, so Import is offered only after a clean Check of exactly these
 * inputs — any change to the gateway, terminal or file throws the check away.
 *
 * `onClose(changed)` is told whether any wallet was created, so the grid
 * behind can refresh.
 */
export default function WalletImportDialog({
    open,
    onClose,
    server,
    terminalsServer,
    gateways,
    requisitesTemplates = [],
    canPickTerminal = true
}) {
    const [gateway, setGateway] = useState(null);
    const [terminal, setTerminal] = useState(null);
    const [file, setFile] = useState(null);

    const [terminals, setTerminals] = useState([]);
    const [terminalsLoading, setTerminalsLoading] = useState(canPickTerminal);
    const [terminalsError, setTerminalsError] = useState(null);

    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState([]);
    const [stage, setStage] = useState(null);
    const [changed, setChanged] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [showSkipped, setShowSkipped] = useState(false);

    const busy = stage !== null;

    useEffect(() => {
        if (!canPickTerminal) return undefined;
        let cancelled = false;
        const load = async () => {
            try {
                const response = await terminalsServer.search(TERMINALS_PAGE_SIZE, [], [], [
                    { field: 'isArchived', op: '!=', value: true }
                ]);
                if (cancelled) return;
                if (hasError(response)) {
                    setTerminalsError(formatApiErrors(response).join('\n'));
                } else {
                    setTerminals(Array.isArray(response.data) ? response.data : []);
                }
            } catch (e) {
                if (!cancelled) setTerminalsError(i18n.t('text.unexpectedError'));
            } finally {
                if (!cancelled) setTerminalsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [terminalsServer, canPickTerminal]);

    const gatewayOptions = useMemo(
        () => (gateways || []).filter((item) => item && !item.isArchived).sort(byName),
        [gateways]
    );

    // One gateway to choose from — a gateway user's own — is chosen for them.
    useEffect(() => {
        if (gatewayOptions.length === 1) {
            setGateway((current) => current ?? gatewayOptions[0]);
        }
    }, [gatewayOptions]);

    const terminalOptions = useMemo(
        () => terminalsForGateway(terminals, gateway?.id),
        [terminals, gateway]
    );

    /** Any input change: whatever was checked no longer describes what would be imported. */
    const discardOutcome = () => {
        setResult(null);
        setErrors([]);
    };

    const changeGateway = (value) => {
        setGateway(value);
        setTerminal(null);
        discardOutcome();
    };

    const changeTerminal = (value) => {
        setTerminal(value);
        discardOutcome();
    };

    const pickFile = (event) => {
        const picked = event.target.files?.[0] || null;
        // Cleared so that picking the same file again (after editing it) still
        // fires onChange.
        event.target.value = '';
        if (!picked) return;

        discardOutcome();
        if (picked.size > MAX_FILE_BYTES) {
            setFile(null);
            setErrors([i18n.t('walletCsv.fileTooLarge', { name: picked.name })]);
            return;
        }
        setFile(picked);
    };

    const run = async (dryRun) => {
        if (!gateway || !file || busy) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('gatewayId', String(gateway.id));
        if (terminal) formData.append('merchantTerminalId', String(terminal.id));
        formData.append('dryRun', dryRun ? 'true' : 'false');

        setStage(dryRun ? STAGE.CHECK : STAGE.IMPORT);
        setErrors([]);
        try {
            const response = await server.importCsv(formData);
            if (hasError(response)) {
                // A whole-file problem (bad gateway, empty file, duplicate
                // header...) — there is no per-row result to show.
                setResult(null);
                setErrors(formatApiErrors(response));
                return;
            }
            const outcome = normalizeResult(response.data);
            setResult(outcome);
            setShowSkipped(outcome.skipped.length <= SKIPPED_SHOWN_OPEN);
            if (!outcome.dryRun && outcome.created > 0) setChanged(true);
        } catch (e) {
            console.error('Wallet CSV import error:', e);
            setResult(null);
            setErrors([i18n.t('text.unexpectedError')]);
        } finally {
            setStage(null);
        }
    };

    const close = () => {
        if (busy) return;
        onClose(changed);
    };

    // The gateway's requisites template, if it names one: its fields are then the
    // only receiver_info columns the import takes.
    const template = templateFor(gateway?.id, gateways, requisitesTemplates);

    const canCheck = Boolean(gateway && file) && !busy;
    // discardOutcome() on every input change is what ties this to "a check of
    // exactly these inputs": a result is never left standing for other inputs.
    const canImport = !busy && isImportReady(result);

    const renderHelp = () => (
        <Box>
            <Button
                size="small"
                onClick={() => setShowHelp((value) => !value)}
                startIcon={
                    <Iconify
                        icon={showHelp ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                    />
                }
            >
                {i18n.t('walletCsv.formatHelp')}
            </Button>
            <Collapse in={showHelp} unmountOnExit>
                <Stack spacing={0.75} sx={{ pt: 1, px: 1 }}>
                    {['helpHeader', 'helpColumns', 'helpIsActive', 'helpIgnored', 'helpSeparator', 'helpLimits'].map(
                        (key) => (
                            <Typography key={key} variant="body2" color="text.secondary">
                                {i18n.t(`walletCsv.${key}`)}
                            </Typography>
                        )
                    )}
                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'background.neutral',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            overflowX: 'auto'
                        }}
                    >
                        {exampleCsv(template)}
                    </Box>
                </Stack>
            </Collapse>
        </Box>
    );

    const renderResult = () => {
        if (!result) return null;
        const { dryRun, rows, toCreate, created, skipped, receiverInfoKeys } = result;
        const rowErrors = result.errors;

        const counts = [
            ['rows', rows],
            [dryRun ? 'toCreate' : 'created', dryRun ? toCreate : created],
            ['skipped', skipped.length],
            ['errors', rowErrors.length]
        ];

        return (
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    {counts.map(([key, value]) => (
                        <Box key={key}>
                            <Typography variant="caption" color="text.secondary">
                                {i18n.t(`walletCsv.count.${key}`)}
                            </Typography>
                            <Typography variant="h6">{fNumber(value)}</Typography>
                        </Box>
                    ))}
                </Stack>

                {receiverInfoKeys.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('walletCsv.receiverInfoKeys')}:{' '}
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>
                            {receiverInfoKeys.join(', ')}
                        </Box>
                    </Typography>
                )}

                {rowErrors.length > 0 && (
                    <Alert severity="error">
                        <AlertTitle>
                            {i18n.t(dryRun ? 'walletCsv.rowErrorsTitle' : 'walletCsv.rowErrorsImportTitle', {
                                n: fNumber(rowErrors.length)
                            })}
                        </AlertTitle>
                        <RowMessagesTable rows={rowErrors} />
                    </Alert>
                )}

                {rowErrors.length === 0 && !dryRun && created > 0 && (
                    <Alert severity="success">
                        {i18n.t('walletCsv.createdWallets', { n: fNumber(created) })}
                    </Alert>
                )}

                {rowErrors.length === 0 && dryRun && toCreate > 0 && (
                    <Alert severity="info">
                        {i18n.t('walletCsv.readyToImport', { n: fNumber(toCreate) })}
                    </Alert>
                )}

                {rowErrors.length === 0 && (dryRun ? toCreate : created) === 0 && (
                    <Alert severity="warning">{i18n.t('walletCsv.nothingToImport')}</Alert>
                )}

                {skipped.length > 0 && (
                    <Box>
                        <Button
                            size="small"
                            color="inherit"
                            onClick={() => setShowSkipped((value) => !value)}
                            startIcon={
                                <Iconify
                                    icon={showSkipped ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                                />
                            }
                        >
                            {i18n.t('walletCsv.skippedRows', { n: fNumber(skipped.length) })}
                        </Button>
                        <Collapse in={showSkipped} unmountOnExit>
                            <RowMessagesTable rows={skipped} />
                        </Collapse>
                    </Box>
                )}
            </Stack>
        );
    };

    return (
        <Dialog open={Boolean(open)} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>{i18n.t('walletCsv.importTitle')}</DialogTitle>

            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    <Autocomplete
                        value={gateway}
                        onChange={(event, value) => changeGateway(value)}
                        options={gatewayOptions}
                        getOptionLabel={(option) => String(option?.name ?? '')}
                        isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
                        renderOption={({ key: _key, ...props }, option) => (
                            <li {...props} key={option.id}>
                                {option.name}
                            </li>
                        )}
                        disabled={busy}
                        renderInput={(params) => (
                            <TextField {...params} label={i18n.t('walletCsv.gateway')} required />
                        )}
                    />

                    {template && (
                        <Alert severity="info" variant="outlined">
                            {i18n.t('walletCsv.templateColumns', {
                                template: i18n.t(`walletRequisites.templates.${template.template}`),
                                columns: template.fields.map((f) => (f.required ? `${f.key}*` : f.key)).join(', ')
                            })}
                        </Alert>
                    )}

                    {canPickTerminal && (
                        <Autocomplete
                            value={terminal}
                            onChange={(event, value) => changeTerminal(value)}
                            options={terminalOptions}
                            getOptionLabel={(option) => String(option?.name ?? '')}
                            isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
                            renderOption={({ key: _key, ...props }, option) => (
                                <li {...props} key={option.id}>
                                    <Box>
                                        <Typography variant="body2">{option.name}</Typography>
                                        {option.merchant?.name && (
                                            <Typography variant="caption" color="text.secondary">
                                                {option.merchant.name}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            loading={terminalsLoading}
                            disabled={busy || !gateway}
                            noOptionsText={i18n.t('walletCsv.noTerminals')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={i18n.t('walletCsv.merchantTerminal')}
                                    helperText={i18n.t('walletCsv.terminalHelper')}
                                />
                            )}
                        />
                    )}

                    {canPickTerminal && terminalsError && (
                        <Alert severity="warning" sx={{ whiteSpace: 'pre-line' }}>
                            {i18n.t('walletCsv.terminalsLoadFailed')}: {terminalsError}
                        </Alert>
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            component="label"
                            variant="outlined"
                            disabled={busy}
                            startIcon={<Iconify icon="eva:file-text-outline" />}
                            sx={{ flexShrink: 0 }}
                        >
                            {i18n.t('walletCsv.chooseFile')}
                            <input type="file" accept=".csv,text/csv" hidden onChange={pickFile} />
                        </Button>
                        <Typography
                            variant="body2"
                            color={file ? 'text.primary' : 'text.secondary'}
                            noWrap
                            title={file?.name || ''}
                        >
                            {file ? file.name : i18n.t('walletCsv.noFile')}
                        </Typography>
                    </Stack>

                    {renderHelp()}

                    {errors.length > 0 && (
                        <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
                            {errors.join('\n')}
                        </Alert>
                    )}

                    {renderResult()}
                </Stack>
            </DialogContent>

            <DialogActions>
                {/* Cancel, as in every other dialog, until wallets have been
                    created; then there is nothing left to cancel. */}
                <Button color="inherit" onClick={close} disabled={busy}>
                    {i18n.t(changed ? 'buttons.close' : 'buttons.cancel')}
                </Button>
                <LoadingButton
                    variant="outlined"
                    loading={stage === STAGE.CHECK}
                    disabled={!canCheck}
                    onClick={() => run(true)}
                >
                    {i18n.t('walletCsv.check')}
                </LoadingButton>
                <LoadingButton
                    variant="contained"
                    loading={stage === STAGE.IMPORT}
                    disabled={!canImport}
                    onClick={() => run(false)}
                >
                    {i18n.t('walletCsv.import')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
